import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ArrowUpRight, Check, Clock3, Copy, Send, X } from "lucide-react"
import { toast } from "sonner"
import type { RunDetail } from "@vienna/contracts"
import { RUN_STATUS_LABELS } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { AutoHeading } from "@/components/automations/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime, initials } from "@/lib/format"
import { runQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/runs/$runId")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(runQuery(params.workspace, params.runId)),
  component: RunDetailPage,
})

function timeOf(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
}

/** Screen 23. */
function RunDetailPage() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const { runId } = Route.useParams()
  const queryClient = useQueryClient()
  const detail = useSuspenseQuery(runQuery(workspace.slug, runId)).data
  const [checked, setChecked] = useState(false)
  const params = { workspace: workspace.slug }
  const { run, steps } = detail
  const uncertain = steps.find((s) => s.intent?.submissionStatus === "unknown")
  const failedStep = steps.find((s) => s.intent?.submissionStatus === "rejected")
  const focus = uncertain ?? failedStep ?? [...steps].reverse().find((s) => s.intent) ?? null
  const terminal = ["completed", "stopped", "failed"].includes(run.status)
  const stop = useMutation({
    mutationFn: () => api<RunDetail>(`/w/${workspace.slug}/automations/runs/${runId}/stop`, { method: "POST" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "runs"] })
      toast("Run stopped", { description: "Future steps suppressed. Anything already in flight keeps its delivery state." })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not stop the run"),
  })
  return (
    <div className="page-wrap auto-page">
      <AutoHeading
        title="A closer look at this run"
        description={`${run.automationName} · v${run.versionNumber} · ${run.id.slice(-8)}`}
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations/runs" params={params} search={{ automationId: run.automationId }}>
              <ArrowLeft />
              Run history
            </Link>
          </Button>
        }
      >
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/automations/runs" params={params} search={{ automationId: run.automationId }}>
            <Clock3 />
            Run history
          </Link>
        </Button>
        {run.conversationId && (
          <Button asChild>
            <Link to="/w/$workspace/inbox" params={params} search={{ c: run.conversationId }}>
              Open conversation
              <ArrowUpRight />
            </Link>
          </Button>
        )}
      </AutoHeading>
      <div className="mb-6 flex items-center gap-3">
        <Avatar>
          <AvatarFallback>{initials(run.contactName)}</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{run.contactName}</p>
          <p className="text-xs text-muted-foreground">{formatDateTime(run.startedAt)}</p>
        </div>
        <Badge variant="outline" className="ml-auto">
          {run.status === "stopped" ? "Run stopped" : run.needsReview ? "Needs review" : RUN_STATUS_LABELS[run.status]}
        </Badge>
      </div>
      {uncertain ? (
        <div className="auto-validation-banner has-warning mb-6">
          <Clock3 className="size-5" />
          <div>
            <h2>{uncertain.title} delivery is unknown</h2>
            <p>The request timed out. Check the delivery record before deciding whether to retry.</p>
          </div>
        </div>
      ) : run.status === "waiting" ? (
        <div className="auto-validation-banner mb-6">
          <Clock3 className="size-5" />
          <div>
            <h2>Waiting at “{detail.cursor.title}”</h2>
            <p>{detail.waitUntil ? `Continues on the timeout branch at ${formatDateTime(detail.waitUntil)} unless a reply arrives.` : "Waiting for the next event."}</p>
          </div>
        </div>
      ) : null}
      <div className="auto-two-col">
        <Card>
          <CardHeader>
            <CardTitle>Run timeline</CardTitle>
            <CardDescription>Every action and the evidence it left behind.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="auto-run-timeline">
              {steps.map((s) => (
                <div key={`${s.nodeId}-${s.executionIndex}`} className={s.intent?.submissionStatus === "unknown" ? "is-uncertain" : ""}>
                  <time>{timeOf(s.occurredAt)}</time>
                  <span className="auto-run-icon">{s.intent?.submissionStatus === "unknown" ? <Clock3 className="size-4" /> : s.outcome === "blocked" || s.intent?.submissionStatus === "rejected" ? <X className="size-4" /> : <Check className="size-4" />}</span>
                  <div>
                    <p className="font-medium">{s.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{s.detail}</p>
                  </div>
                </div>
              ))}
              {!terminal && (
                <div>
                  <time>—</time>
                  <span className="auto-run-icon">
                    <Clock3 className="size-4" />
                  </span>
                  <div>
                    <p className="font-medium">{detail.cursor.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{run.status === "handed_over" ? "Paused until your team releases the conversation" : run.status === "waiting" ? "Waiting" : "Not started"}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Copy />
                  Inspect run
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Run record</DialogTitle>
                  <DialogDescription>References only; provider responses live on the ledger.</DialogDescription>
                </DialogHeader>
                <pre className="overflow-auto rounded-lg bg-muted p-4 text-xs leading-6">{JSON.stringify({ run: run.id, version: run.versionNumber, status: run.status, cursor: detail.cursor, events: detail.eventLog, steps: steps.map((s) => ({ node: s.nodeId, outcome: s.outcome, intent: s.intent?.id ?? null, submission: s.intent?.submissionStatus ?? null })) }, null, 2)}</pre>
              </DialogContent>
            </Dialog>
            <Button variant="ghost" asChild>
              <Link to="/w/$workspace/contacts/$contactId" params={{ ...params, contactId: run.contactId }}>
                Contact profile
                <ArrowUpRight />
              </Link>
            </Button>
          </CardFooter>
        </Card>
        <Card className="self-start">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Send className="size-4" />
              <CardTitle>{focus?.title ?? "Delivery"}</CardTitle>
            </div>
            <CardDescription>{focus ? `Step ${String(focus.executionIndex + 1).padStart(2, "0")} · delivery record` : "No send step in this run"}</CardDescription>
          </CardHeader>
          <CardContent>
            {focus?.intent ? (
              <dl className="space-y-5">
                {[
                  ["Delivery status", focus.intent.submissionStatus === "unknown" ? "Unknown · awaiting reconciliation" : focus.intent.submissionStatus === "accepted" ? "Accepted by the provider" : focus.intent.submissionStatus === "rejected" ? "Rejected" : focus.intent.submissionStatus === "cancelled" ? "Cancelled" : "Queued"],
                  ["Provider response", focus.intent.providerMessageId ? `Reference ${focus.intent.providerMessageId}` : (focus.intent.reason ?? (focus.intent.submissionStatus === "unknown" ? "Request timed out" : "None yet"))],
                  ["Attempts", `${focus.intent.attempts}${focus.intent.lastAttemptAt ? ` · last ${formatDateTime(focus.intent.lastAttemptAt)}` : ""}`],
                  ["Completed steps", "Preserved · will not be repeated"],
                ].map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-xs text-muted-foreground">{key}</dt>
                    <dd className="mt-1.5 text-sm">{value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">This run has not sent anything.</p>
            )}
            <Separator className="my-6" />
            <h3 className="font-medium">{detail.canRetry ? "Retry is available" : "Why retry is unavailable"}</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{detail.retryBlockedReason ?? "The last send was refused by the provider, so a retry cannot duplicate a message."}</p>
            <Button
              className="mt-5 w-full"
              variant="outline"
              onClick={() => {
                void queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "runs", runId] })
                setChecked(true)
              }}
            >
              Check delivery status
            </Button>
            {checked && (
              <p role="status" className="mt-2 text-xs text-muted-foreground">
                Checked just now · {uncertain ? "no new delivery receipt." : "record is current."}
              </p>
            )}
            <div className="mt-2 flex gap-2">
              <Button variant="outline" disabled className="flex-1">
                Retry this step
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" disabled={terminal || !can("edit_automations")} className="flex-1">
                    {run.status === "stopped" ? "Run stopped" : "Stop run"}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Stop this run?</DialogTitle>
                    <DialogDescription>Future steps will be suppressed. A message already in flight may still arrive; its delivery remains as recorded.</DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Keep running</Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button onClick={() => stop.mutate()}>Stop future steps</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
